// Thin client for the Artifacts library endpoints. Bearer auth via the shell's token (sessionStorage,
// not cookie) — matches the rest of the Ros API calls. Keeps fetch/JSON plumbing out of the page.

import type { SessionArtifact } from '../components/rosArtifacts'

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

/** All of the user's published artifacts (grouped → flattened by the caller). */
export async function fetchArtifacts(apiBaseUrl: string, token: string | null, fetcher: typeof fetch = fetch): Promise<Array<{ type: string; items: SessionArtifact[] }>> {
  const res = await fetcher(`${apiBaseUrl}/api/r3/artifacts`, { credentials: 'include', headers: authHeaders(token) })
  if (!res.ok) throw new Error(`artifacts ${res.status}`)
  const data = await res.json() as { groups?: Array<{ type: string; items: SessionArtifact[] }> }
  return data.groups ?? []
}

/** Extend one version's retention window (backend clamps to [current, MAX]). Returns new retentionDays. */
export async function extendRetention(
  apiBaseUrl: string, token: string | null, artifactId: string, version: number, retentionDays: number, fetcher: typeof fetch = fetch,
): Promise<number> {
  const res = await fetcher(`${apiBaseUrl}/api/r3/artifacts/${artifactId}/versions/${version}/retention`, {
    method: 'PATCH', credentials: 'include', headers: authHeaders(token),
    body: JSON.stringify({ retentionDays }),
  })
  if (!res.ok) throw new Error(`extend ${res.status}`)
  const data = await res.json() as { retentionDays: number }
  return data.retentionDays
}

/** Delete an artifact and all its versions. */
export async function deleteArtifact(apiBaseUrl: string, token: string | null, artifactId: string, fetcher: typeof fetch = fetch): Promise<void> {
  const res = await fetcher(`${apiBaseUrl}/api/r3/artifacts/${artifactId}`, {
    method: 'DELETE', credentials: 'include', headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(`delete ${res.status}`)
}

export async function createArtifactChat(
  apiBaseUrl: string,
  token: string | null,
  artifact: Pick<SessionArtifact, 'id' | 'title'>,
  fetcher: typeof fetch = fetch,
): Promise<string> {
  const headers: Record<string, string> = {'Content-Type': 'application/json'}
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetcher(`${apiBaseUrl}/api/r3/artifacts/${encodeURIComponent(artifact.id)}/chat`, {
    method: 'POST', credentials: 'include', headers, body: JSON.stringify({title: artifact.title}),
  })
  const result = await response.json().catch(() => ({})) as {sessionId?: string; error?: string}
  if (!response.ok || !result.sessionId) throw new Error(result.error || `Could not create chat (${response.status})`)
  return result.sessionId
}
